<template>
  <div class="card listing">
    <div class="card-top">
      <p>
        <router-link
          :to="{ name: 'profile', params: { id: listing.user.id } }"
          class="body-color"
        >
          <avatar :user="listing.user" />
          {{ listing.user.first_name }} {{ listing.user.last_name }}
        </router-link>

        posted a listing
      </p>

      <div
        v-if="userData"
        class="star-and-menu"
      >
        <star :post="listing" />
        <item-menu :listing="listing" />
      </div>
    </div>

    <span
      v-if="listing.status === 'active'"
      class="price"
    >
      {{ formatPrice(listing.price) }}
    </span>
    <span
      v-else-if="listing.status ==='pending'"
      class="price pending"
    >
      Pending - {{ formatPrice(listing.price) }}
    </span>
    <span
      v-else
      class="price sold"
    >
      Sold - {{ formatPrice(listing.price) }}
    </span>

    <span class="title">
      {{ listing.title }}
    </span>

    <auction-time
      v-if="listing.auction_end"
      :auction-end="listing.auction_end"
    />

    <div class="description small">
      <pre>{{ description }}</pre>
      <p v-if="link">
        <a
          :href="link"
          target="_blank"
        >{{ link }}</a>
      </p>
      <div class="tags">
        <span
          v-for="(tag, index) in listing.tags"
          :key="'tag' + index"
          @click.prevent="$router.push({ name: 'tagged', params: { name: tag.name } })"
        >
          #{{ tag.name }}
        </span>
      </div>
    </div>

    <div
      v-if="listing.images.length"
      class="image stretched"
    >
      <images
        :images="listing.images"
        :detail="detail"
      />
    </div>

    <div class="location-and-message">
      <div class="location">
        <p>Location</p>

        {{ listing.location.city_name }}
        [<a
          :href="`https://www.google.com/maps/place/${listing.zip_code}`"
          target="_blank"
        >
          map
        </a>]
      </div>

      <button
        v-if="userData && userData.id !== listing.user.id"
        type="button"
        class="btn btn-primary btn-sm"
        @click="message"
      >
        Message
      </button>
      <template v-if="userData && userData.id === listing.user.id">
        <button
          type="button"
          class="btn btn-primary btn-sm"
          @click="message"
        >
          View Messages
        </button>
        <mark-as-sold
          :listing="listing"
          @newStatus="updateStatus"
        />
      </template>
    </div>

    <hr class="stretched">

    <div class="stats">
      <button
        type="button"
        class="btn btn-transparent"
        @click="showComments = !showComments"
      >
        {{ pluralize(listing.total_comments, 'comment') }}
      </button>

      <total-likes
        :comment="false"
        :likes="listing.likes"
        :total-likes="listing.total_likes"
      />

      <span>{{ pluralize(listing.total_views, 'view') }}</span>

      <span>{{ dateFromNow(listing.created) }}</span>
    </div>

    <div class="actions">
      <template v-if="userData">
        <like :listing="listing" />

        <button
          type="button"
          class="comment-button btn btn-transparent"
          @click="comment"
        >
          <comment-outline-icon />
          <span class="small">Comment</span>
        </button>
      </template>

      <share />
    </div>

    <comments
      v-show="showComments"
      ref="comments"
      object-type="listing"
      :object-id="listing.id"
      :comments="listing.comments"
    />
  </div>
</template>

<script>
import AuctionTime from '@/components/AuctionTime.vue';
import Avatar from '@/components/Avatar.vue';
import Comments from '@/components/Comments.vue';
import Images from '@/components/Images.vue';
import ItemMenu from '@/components/ItemMenu.vue';
import Like from '@/components/Like.vue';
import MarkAsSold from '@/components/MarkAsSold.vue';
import Share from '@/components/Share.vue';
import Star from '@/components/Star.vue';
import TotalLikes from '@/components/TotalLikes.vue';

export default {
  components: {
    AuctionTime,
    Avatar,
    Comments,
    Images,
    ItemMenu,
    Like,
    MarkAsSold,
    Share,
    Star,
    TotalLikes,
  },
  props: {
    listing: {
      type: Object,
      required: true,
    },
    detail: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      showComments: this.detail,
      link: '',
    };
  },
  computed: {
    description() {
      let desc = this.listing.description.trim();
      if (this.listing.user.first_name === 'GunGroupDeals' && desc.includes('http')) {
        const firstHalf = desc.slice(0, desc.indexOf('Check'));
        const link = desc.slice(desc.indexOf('http'), desc.indexOf('. Shipping'));
        this.setLink(link);
        desc = `${firstHalf}Shipping options available!`;
      }
      return desc;
    },
    listingUrl() {
      return `https://www.gungroup.com${this.$route.fullPath}`;
    },
  },
  title() {
    return this.listing.title || 'Page Not Found';
  },
  metaInfo() {
    if (this.listing.id) {
      let image;

      if (this.listing.images.length) {
        image = this.listing.images[0].url;
      }

      return {
        meta: [
          { property: 'og:type', content: 'product' },
          { property: 'og:url', content: this.listingUrl },
          { property: 'og:title', content: this.listing.title },
          { property: 'og:description', content: this.listing.description },
          { property: 'og:image', content: image },
        ],
      };
    }

    return {};
  },
  methods: {
    setLink(link) {
      this.link = link;
    },
    comment() {
      if (this.showComments) {
        const commentTextarea = this.$refs.comments.$el.querySelector('.js-comment-textarea');

        if (this.isElementInViewport(commentTextarea)) {
          commentTextarea.focus();
        } else {
          commentTextarea.scrollIntoView({
            behavior: 'smooth',
            block: 'center',
          });

          setTimeout(() => {
            commentTextarea.focus();
          }, 1000);
        }
      } else {
        this.showComments = true;
      }
    },
    message() {
      this.$router.push({ name: 'message_listing', params: { id: this.listing.id } });
    },
    updateStatus(newStatus) {
      this.listing.status = newStatus;
    },
  },
};
</script>

<style lang="scss" scoped>
  .location-and-message {
    display: grid;
    grid-gap: 0.5rem;
    grid-template-columns: 1fr auto;
    align-items: center;
    margin-bottom: 0.5rem;
  }

  .location {
    font-size: 80%;

    p {
      margin-bottom: 0;
      font-weight: bold;
    }
  }

  .tags {
    padding-top: 8px;

    span:hover {
      cursor: pointer;
      color: var(--blue);
    }
  }
</style>
